import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Dimmer, Loader } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useDevicesQuery, useUpdateDeviceMutation } from "modules/UsersDashboard/components/EditUserModal/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { Device } from "models";

type DevicesTableProps = {
  userId: number | null;
};

const columnHelper = createColumnHelper<Device>();

export const DevicesTable = ({ userId }: DevicesTableProps) => {
  const { t } = useTranslation();

  const canToggleLease = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DevicesLease);
  const { data, isFetching } = useDevicesQuery(userId);
  const { mutateAsync: updateDeviceMutation } = useUpdateDeviceMutation();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "lease",
        header: () => <span> {t("v8_lease", "Lease")} </span>,
        size: 40,
        cell: ({
          row: {
            original: { is_leased, id }
          }
        }) => (
          <div className="lease-toggle">
            <Checkbox
              toggle
              checked={is_leased}
              disabled={!canToggleLease}
              onChange={async () => {
                await updateDeviceMutation({ device_id: id, is_leased: !is_leased });
              }}
            />
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "last_used",
        header: () => <span> {t("v8_last_used", "Last Used")} </span>,
        cell: ({
          row: {
            original: { last_used }
          }
        }) => (
          <span className="no-wrap-text">
            {formatDate(last_used, DATE_FORMATS_FNS.dateMonthYear)} | {formatDate(last_used, DATE_FORMATS_FNS.hoursMinutesSeconds)}
          </span>
        ),
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "brand",
        header: () => <span> {t("v8_brand", "Brand")} </span>,
        cell: ({
          row: {
            original: { brand }
          }
        }) => <span className="no-wrap-text">{brand}</span>,
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "model",
        header: () => <span> {t("v8_model", "Model")} </span>,
        cell: ({
          row: {
            original: { model }
          }
        }) => <span className="no-wrap-text">{model}</span>,
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "os",
        header: () => <span> {t("v8_os", "OS")} </span>,
        cell: ({
          row: {
            original: { os_version }
          }
        }) => <span className="no-wrap-text">{os_version}</span>,
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "free_space",
        header: () => <span> {t("v8_free_space", "Free Space")} </span>,
        cell: ({
          row: {
            original: { free_space }
          }
        }) => <span className="no-wrap-text">{free_space}</span>,
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "mac_address",
        header: () => <span> {t("v8_mac_address", "MAC Address")} </span>,
        cell: ({
          row: {
            original: { mac_address }
          }
        }) => <span className="no-wrap-text">{mac_address}</span>,
        size: 100
      })
    ],
    [canToggleLease, t, updateDeviceMutation]
  );

  return (
    <Dimmer.Dimmable dimmed={isFetching}>
      <Dimmer active={isFetching} inverted>
        <Loader active inline="centered" />
      </Dimmer>
      <ReactTable columns={columns} data={data} />
    </Dimmer.Dimmable>
  );
};
